/**
 * Clarity Tracker — visitor analytics + session-replay-lite.
 * Implements the "Clarity Tracker — Frontend Integration Guide".
 *
 * Public API:
 *   Clarity.track(type, data)          push a custom activity event
 *   Clarity.identify(email, name, phone, meta)
 *   Clarity.formSubmit({ name, selector, fields, was_complete })
 *   Clarity.flush()                    force-flush the event buffer
 *   Clarity.spaRouteChange()           call after client-side navigation
 *   Clarity.getSessionUuid() / getPageViewUuid() / getVisitorUuid()
 */
(function () {
  'use strict';

  var API_BASE = 'https://clarity.marke-ter.online/api/tracker';
  var SESSION_TIMEOUT_MS = 30 * 60 * 1000;
  var FLUSH_INTERVAL_MS = 5 * 1000;
  var HEARTBEAT_INTERVAL_MS = 30 * 1000;
  var MAX_BATCH = 500;
  var GEO_TIMEOUT_MS = 2000;
  // FingerprintJS is only the storage-disabled fallback (see FP_* below) —
  // it never replaces the cookie/localStorage/sessionStorage session id.
  var FP_CDN_URL = 'https://openfpcdn.io/fingerprintjs/v5/iife.min.js';
  var FP_LOAD_TIMEOUT_MS = 3000;
  var FP_GET_TIMEOUT_MS = 1500;
  var FILE_MAX_BYTES = 20 * 1024 * 1024;
  var OK_FILE_MIMES = ['image/png', 'image/jpeg', 'image/svg+xml', 'application/pdf'];
  var BLOCKED_COUNTRIES = ['PK'];
  // URL paths for which tracking is skipped entirely.
  var BLOCKED_PATH_SUBSTRINGS = ['/products/'];

  function isPathBlocked(path) {
    if (!path) return false;
    for (var i = 0; i < BLOCKED_PATH_SUBSTRINGS.length; i++) {
      if (path.indexOf(BLOCKED_PATH_SUBSTRINGS[i]) !== -1) return true;
    }
    return false;
  }

  // ---------- storage helpers ----------
  function setCookie(name, value, days) {
    try {
      var d = new Date();
      d.setTime(d.getTime() + days * 86400000);
      document.cookie = name + '=' + encodeURIComponent(value) +
        ';expires=' + d.toUTCString() + ';path=/;SameSite=Lax';
    } catch (_) {}
  }
  function getCookie(name) {
    var m = document.cookie.match('(?:^|; )' + name + '=([^;]*)');
    return m ? decodeURIComponent(m[1]) : null;
  }
  function ssGet(k) { try { return sessionStorage.getItem(k); } catch (_) { return null; } }
  function ssSet(k, v) { try { sessionStorage.setItem(k, v); } catch (_) {} }
  function ssRemove(k) { try { sessionStorage.removeItem(k); } catch (_) {} }
  function lsGet(k) { try { return localStorage.getItem(k); } catch (_) { return null; } }
  function lsSet(k, v) { try { localStorage.setItem(k, v); } catch (_) {} }
  function lsRemove(k) { try { localStorage.removeItem(k); } catch (_) {} }

  // ---------- debug ----------
  var DEBUG = !!(typeof window !== 'undefined' && window.ClarityDebug);
  function debugLog() {
    if (!DEBUG || typeof console === 'undefined') return;
    try { console.log.apply(console, ['[Tracker]'].concat(Array.prototype.slice.call(arguments))); } catch (_) {}
  }

  function uuid() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = Math.random() * 16 | 0;
      var v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  function nowIso() { return new Date().toISOString(); }

  // ---------- identity ----------
  // Priority: cookie -> localStorage -> generate new. Re-persisted to both on
  // every load (best-effort) so whichever storage is actually available keeps it.
  var visitorUuid = getCookie('clarity_visitor_uuid') || lsGet('clarity_visitor_uuid');
  if (!visitorUuid) {
    visitorUuid = uuid();
    debugLog('New visitor', visitorUuid);
  }
  setCookie('clarity_visitor_uuid', visitorUuid, 730);
  lsSet('clarity_visitor_uuid', visitorUuid);

  var sessionUuid = null;
  var pageViewUuid = null;
  var previousPageViewUuid = ssGet('clarity_page_view_uuid') || null;
  var pageEnteredAtMs = Date.now();
  var maxScroll = 0;
  var isBlocked = false;         // hard block (geo) — disables everything permanently
  var pageBlocked = false;       // soft block (per-pageview URL) — flips on SPA nav
  var listenersAttached = false;
  var intervalsStarted = false;

  // Priority: sessionStorage -> localStorage -> (fingerprint fallback, resolved
  // later via startSession() when this comes up empty).
  var lastActivityRaw = ssGet('clarity_last_activity_ts') || lsGet('clarity_last_activity_ts');
  var lastActivityMs = lastActivityRaw ? Date.parse(lastActivityRaw) : 0;
  var sessionStale = !lastActivityRaw || (Date.now() - lastActivityMs) > SESSION_TIMEOUT_MS;
  if (!sessionStale) sessionUuid = ssGet('clarity_session_uuid') || lsGet('clarity_session_uuid');
  debugLog('Cookie/localStorage session:', sessionUuid || 'unavailable');

  function touch() {
    var t = nowIso();
    ssSet('clarity_last_activity_ts', t);
    lsSet('clarity_last_activity_ts', t);
  }

  // ---------- FingerprintJS (storage-disabled fallback only) ----------
  // Loaded from the official CDN as a plain <script> (no bundler, no npm
  // package) and initialized once; never invoked per-event, only when
  // startSession() has no cookie/localStorage/sessionStorage session to reuse.
  var fpPromise = null;

  function loadScript(src, timeoutMs) {
    return new Promise(function (resolve, reject) {
      var settled = false;
      var t = setTimeout(function () {
        if (settled) return;
        settled = true;
        reject(new Error('fp_load_timeout'));
      }, timeoutMs);
      var s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.onload = function () {
        if (settled) return;
        settled = true;
        clearTimeout(t);
        resolve();
      };
      s.onerror = function () {
        if (settled) return;
        settled = true;
        clearTimeout(t);
        reject(new Error('fp_load_error'));
      };
      document.head.appendChild(s);
    });
  }

  function initFingerprint() {
    if (fpPromise) return fpPromise;
    fpPromise = loadScript(FP_CDN_URL, FP_LOAD_TIMEOUT_MS)
      .then(function () {
        if (!window.FingerprintJS) throw new Error('fp_unavailable');
        return window.FingerprintJS.load();
      })
      .then(function (fp) {
        debugLog('FingerprintJS initialized');
        return fp;
      })
      .catch(function (err) {
        debugLog('FingerprintJS unavailable', err && err.message);
        return null;
      });
    return fpPromise;
  }

  // Returns { visitor_id, confidence, version } or null — never throws/rejects.
  function getFingerprint() {
    return initFingerprint().then(function (fp) {
      if (!fp) return null;
      var timeoutP = new Promise(function (resolve) {
        setTimeout(function () { resolve(null); }, FP_GET_TIMEOUT_MS);
      });
      var resultP = fp.get().then(function (result) {
        debugLog('Fingerprint visitorId received', result.visitorId, 'confidence', result.confidence && result.confidence.score);
        return {
          visitor_id: result.visitorId,
          confidence: result.confidence ? result.confidence.score : null,
          version: result.version || null,
        };
      }).catch(function () { return null; });
      return Promise.race([resultP, timeoutP]);
    }).catch(function () { return null; });
  }

  // ---------- event buffer ----------
  var buffer = [];

  function push(type, extra) {
    if (isBlocked || pageBlocked) return;
    if (buffer.length >= MAX_BATCH) buffer.shift();
    var evt = { type: type, occurred_at: nowIso() };
    if (extra) for (var k in extra) if (Object.prototype.hasOwnProperty.call(extra, k)) evt[k] = extra[k];
    buffer.push(evt);
    touch();
  }

  // ---------- HTTP ----------
  function post(path, body, useBeacon) {
    var url = API_BASE + path;
    var payload = JSON.stringify(body);

    if (useBeacon && navigator.sendBeacon) {
      try {
        var blob = new Blob([payload], { type: 'application/json' });
        var ok = navigator.sendBeacon(url, blob);
        if (ok) return Promise.resolve(null);
      } catch (_) {}
    }

    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: payload,
      keepalive: true,
    }).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.status === 204 ? null : r.json().catch(function () { return null; });
    });
  }

  // ---------- IP-geo (best-effort, bounded by GEO_TIMEOUT_MS) ----------
  function fetchGeoWithTimeout() {
    var timeoutP = new Promise(function (res) { setTimeout(function () { res(null); }, GEO_TIMEOUT_MS); });

    var geoP = fetch('https://ipapi.co/json/', { credentials: 'omit' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        if (!d || !d.country) return null;
        return {
          country: (d.country || '').toUpperCase() || null,
          region: d.region || null,
          city: d.city || null,
          timezone: d.timezone || null,
        };
      })
      .catch(function () { return null; });

    return Promise.race([geoP, timeoutP]);
  }

  // ---------- session + pageview ----------
  function startSession(geo, fp) {
    var params = new URLSearchParams(location.search);
    var tz = null;
    try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch (_) {}

    var body = {
      visitor_uuid: visitorUuid,
      entry_url: location.href,
      referrer: document.referrer || null,
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_term: params.get('utm_term'),
      utm_content: params.get('utm_content'),
      screen_width: screen.width,
      screen_height: screen.height,
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      language: navigator.language,
      timezone: (geo && geo.timezone) || tz,
    };
    if (geo) {
      body.country = geo.country;
      body.region = geo.region;
      body.city = geo.city;
    }
    if (fp && fp.visitor_id) {
      body.fingerprint_id = fp.visitor_id;
      body.fingerprint_confidence = fp.confidence;
      body.fingerprint_version = fp.version;
    }

    debugLog('Sending session request', fp ? 'with fingerprint fallback' : 'without fingerprint');

    return post('/session', body).then(function (r) {
      if (r && r.session_uuid) {
        sessionUuid = r.session_uuid;
        ssSet('clarity_session_uuid', sessionUuid);
        lsSet('clarity_session_uuid', sessionUuid);
        debugLog('Server resolved session', r.session_status, 'identification method:', r.identification_method);
      }
    });
  }

  function openPageView() {
    pageEnteredAtMs = Date.now();
    maxScroll = 0;
    return post('/pageview', {
      session_uuid: sessionUuid,
      url: location.href,
      url_path: location.pathname,
      url_query: location.search.replace(/^\?/, '') || null,
      referrer_url: document.referrer || null,
      page_title: document.title,
      previous_page_view_uuid: previousPageViewUuid,
      entered_at: new Date(pageEnteredAtMs).toISOString(),
    }).then(function (r) {
      if (r && r.page_view_uuid) {
        pageViewUuid = r.page_view_uuid;
        ssSet('clarity_page_view_uuid', pageViewUuid);
      }
      touch();
    });
  }

  // ---------- flush + heartbeat ----------
  function flush(useBeacon) {
    if (isBlocked || !pageViewUuid || buffer.length === 0) return Promise.resolve();
    var events = buffer.splice(0, MAX_BATCH);
    return post('/events', {
      session_uuid: sessionUuid,
      page_view_uuid: pageViewUuid,
      events: events,
    }, useBeacon).catch(function () {
      // Requeue on failure, keeping buffer bounded.
      for (var i = events.length - 1; i >= 0 && buffer.length < MAX_BATCH; i--) {
        buffer.unshift(events[i]);
      }
    });
  }

  function heartbeat() {
    if (isBlocked || !pageViewUuid || document.hidden) return;
    var timeOnPage = Math.floor((Date.now() - pageEnteredAtMs) / 1000);
    post('/heartbeat', {
      session_uuid: sessionUuid,
      page_view_uuid: pageViewUuid,
      time_on_page_seconds: timeOnPage,
      max_scroll_depth: maxScroll,
    }).catch(function () {});
  }

  // ---------- listeners ----------
  function getScrollDepth() {
    var h = document.documentElement;
    var denom = h.scrollHeight || 1;
    return Math.min(100, Math.round(((h.scrollTop + window.innerHeight) / denom) * 100));
  }

  function safeClassName(el) {
    var c = el && el.className;
    if (typeof c === 'string') return c;
    if (c && typeof c.baseVal === 'string') return c.baseVal; // SVG
    return null;
  }

  // ---------- image upload interception ----------
  // The quote form's file input triggers uploads to Cloudinary and to the
  // in-house imagescloud service. We passively listen for their responses
  // and remember the resulting URLs so they can be attached to the form
  // submission as `uploadImagesURL` (and mirrored as a `form_input` event so
  // abandoned-form recovery has them too).
  var UPLOAD_URL_PATTERNS = [
    /api\.cloudinary\.com\/.+\/image\/upload/i,
    /imagescloud\.codeblue\.agency\/api\/image\/upload/i,
  ];
  var uploadedImageUrls = [];

  function isUploadEndpoint(url) {
    if (!url) return false;
    var s = String(url);
    for (var i = 0; i < UPLOAD_URL_PATTERNS.length; i++) {
      if (UPLOAD_URL_PATTERNS[i].test(s)) return true;
    }
    return false;
  }

  function extractUploadUrls(data) {
    var out = [];
    if (!data || typeof data !== 'object') return out;
    // Cloudinary: { secure_url, url, ... }
    if (typeof data.secure_url === 'string') out.push(data.secure_url);
    else if (typeof data.url === 'string') out.push(data.url);
    // imagescloud: { imageUrls: [...] }
    if (Array.isArray(data.imageUrls)) {
      for (var i = 0; i < data.imageUrls.length; i++) {
        if (typeof data.imageUrls[i] === 'string') out.push(data.imageUrls[i]);
      }
    }
    return out;
  }

  function recordUploadResponse(rawText) {
    if (!rawText) return;
    var data;
    try { data = JSON.parse(rawText); } catch (_) { return; }
    var urls = extractUploadUrls(data);
    if (!urls.length) return;

    var changed = false;
    for (var i = 0; i < urls.length; i++) {
      if (uploadedImageUrls.indexOf(urls[i]) === -1) {
        uploadedImageUrls.push(urls[i]);
        changed = true;
      }
    }
    if (!changed) return;

    // Mirror the running list as a form_input so abandoned-form recovery
    // shows the uploaded URLs even if the user never submits.
    var joined = uploadedImageUrls.join(',');
    push('form_input', {
      element_name: 'uploadImagesURL',
      form_field_type: 'text',
      meta: { value: joined, length: joined.length },
    });
  }

  // Patch fetch — wrap, never mutate. Only touches upload endpoints.
  (function patchFetch() {
    if (typeof window.fetch !== 'function') return;
    var origFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url);
      var watch = isUploadEndpoint(url);
      var p;
      try { p = origFetch.apply(this, arguments); }
      catch (e) { return Promise.reject(e); }
      if (!watch || !p || typeof p.then !== 'function') return p;
      return p.then(function (res) {
        try {
          if (res && typeof res.clone === 'function') {
            res.clone().text()
              .then(function (t) { try { recordUploadResponse(t); } catch (_) {} })
              .catch(function () {});
          }
        } catch (_) {}
        return res;
      });
    };
  })();

  // Patch XMLHttpRequest — same principle, wrap and observe only upload endpoints.
  (function patchXHR() {
    if (typeof XMLHttpRequest === 'undefined') return;
    var origOpen = XMLHttpRequest.prototype.open;
    var origSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      try { this.__clarityUpload = isUploadEndpoint(url); } catch (_) {}
      return origOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      var xhr = this;
      if (xhr.__clarityUpload) {
        try {
          xhr.addEventListener('load', function () {
            try {
              if (xhr.status >= 200 && xhr.status < 300) {
                recordUploadResponse(xhr.responseText);
              }
            } catch (_) {}
          });
        } catch (_) {}
      }
      return origSend.apply(this, arguments);
    };
  })();

  // ---------- form auto-capture ----------
  // Matches native submit buttons AND common "fake submit" buttons that use
  // type="button" + a custom click handler (e.g. Signize's .submitButton).
  var SUBMIT_BTN_SELECTOR =
    'button[type="submit"], input[type="submit"], input[type="image"], ' +
    '.submitButton, [data-clarity-submit]';
  // Per-form dedupe window: 2 s covers "click fires + custom JS calls form.submit()".
  var recentlySubmitted = new WeakMap();

  function serializeForm(form) {
    var fields = {};
    try {
      var els = form.elements || [];
      for (var i = 0; i < els.length; i++) {
        var el = els[i];
        if (!el || !el.name) continue;
        if (el.disabled) continue;
        if (el.type === 'password') continue;
        if (el.getAttribute && el.getAttribute('data-clarity-ignore') !== null) continue;

        var name = el.name;
        var type = el.type;
        var tag = el.tagName;

        if (type === 'checkbox' || type === 'radio') {
          if (!el.checked) continue;
          var val = (el.value != null && el.value !== '') ? el.value : true;
          if (fields[name] === undefined) {
            fields[name] = val;
          } else {
            if (!Array.isArray(fields[name])) fields[name] = [fields[name]];
            fields[name].push(val);
          }
        } else if (type === 'file') {
          var files = [];
          var fl = el.files || [];
          for (var j = 0; j < fl.length; j++) {
            files.push({ filename: fl[j].name, mime_type: fl[j].type, size_bytes: fl[j].size });
          }
          if (files.length === 1) fields[name] = files[0];
          else if (files.length > 1) fields[name] = files;
        } else if (tag === 'SELECT' && el.multiple) {
          var opts = [];
          var so = el.selectedOptions || [];
          for (var k = 0; k < so.length; k++) opts.push(so[k].value);
          fields[name] = opts;
        } else {
          fields[name] = el.value;
        }
      }
    } catch (_) {}

    // Attach any image URLs captured from Cloudinary / imagescloud uploads
    // as a comma-separated string.
    if (uploadedImageUrls.length && fields.uploadImagesURL === undefined) {
      fields.uploadImagesURL = uploadedImageUrls.join(',');
    }

    return fields;
  }

  function formNameFor(form) {
    return (form.dataset && form.dataset.trackName) ||
           form.getAttribute('name') ||
           form.id ||
           'form';
  }

  function formSelectorFor(form) {
    if (form.id) return '#' + form.id;
    var n = form.getAttribute('name');
    return n ? 'form[name="' + n + '"]' : null;
  }

  function pickField(fields, keys, fuzzyRegex) {
    for (var i = 0; i < keys.length; i++) {
      var v = fields[keys[i]];
      if (typeof v === 'string' && v.length) return v;
    }
    if (fuzzyRegex) {
      for (var k in fields) {
        if (!Object.prototype.hasOwnProperty.call(fields, k)) continue;
        if (fuzzyRegex.test(k) && typeof fields[k] === 'string' && fields[k].length) return fields[k];
      }
    }
    return null;
  }

  function doFormSubmit(form, wasComplete) {
    if (!form || isBlocked || pageBlocked) return;
    if (!sessionUuid || !pageViewUuid) return;

    var now = Date.now();
    var last = recentlySubmitted.get(form);
    if (last && (now - last) < 2000) return; // dedupe submit-vs-click double fire
    recentlySubmitted.set(form, now);

    var fields = serializeForm(form);
    var name = formNameFor(form);
    var selector = formSelectorFor(form);

    push('form_submit_success', {
      meta: { form_name: name, form_selector: selector },
    });

    post('/form-submit', {
      session_uuid: sessionUuid,
      page_view_uuid: pageViewUuid,
      form_name: name,
      form_selector: selector,
      fields: fields,
      was_complete: wasComplete !== false,
      submitted_at: nowIso(),
    }, true);

    var email = pickField(fields, ['email', 'Email', 'EMAIL', 'user_email', 'contact_email', 'email_address'], /email/i);
    if (email) {
      post('/identify', {
        session_uuid: sessionUuid,
        email: email,
        name: pickField(fields, ['name', 'your_name', 'full_name', 'fullName', 'first_name', 'firstName']),
        phone: pickField(fields, ['phone', 'phone_number', 'phoneNumber', 'tel', 'mobile', 'contact_number']),
        meta: null,
      }, true);
    }

    // Flush any pending events with beacon so they survive a full-page nav after submit.
    flush(true);

    // Reset upload accumulator so a subsequent submission on the same page
    // doesn't carry stale URLs.
    uploadedImageUrls = [];
  }

  function trackClick(e) {
    var raw = e.target;
    if (!raw || raw.nodeType !== 1) return;
    // Toggles/selects have their own handler.
    if (raw.closest && raw.closest('[data-toggle],[data-select]')) return;

    var el = (raw.closest && raw.closest('a, button, [data-track], [role="button"], [role="link"], input[type="submit"], input[type="button"]')) || raw;
    var text = (el.innerText || el.textContent || '').trim().slice(0, 512) || null;
    var href = el.href || null;

    push('click', {
      element_tag: (el.tagName || '').toLowerCase() || null,
      element_id: el.id || null,
      element_classes: safeClassName(el),
      element_text: text,
      element_href: href,
      viewport_x: e.clientX,
      viewport_y: e.clientY,
      meta: { x: e.clientX, y: e.clientY, href: href },
    });
  }

  function trackToggleOrSelect(e) {
    var t = e.target;
    if (!t || !t.closest) return;

    var toggle = t.closest('[data-toggle]');
    if (toggle) {
      // Read state on next tick so consumers can flip classes/aria first.
      setTimeout(function () {
        var on = toggle.classList.contains('is-active') ||
                 toggle.getAttribute('aria-pressed') === 'true';
        push('toggle', {
          element_id: toggle.id || null,
          element_tag: toggle.tagName.toLowerCase(),
          meta: {
            group: toggle.dataset.toggle,
            value: toggle.dataset.value || null,
            state: on ? 'on' : 'off',
          },
        });
      }, 0);
      return;
    }

    var sel = t.closest('[data-select]');
    if (sel) {
      setTimeout(function () {
        var selected = sel.classList.contains('is-selected') ||
                       sel.getAttribute('aria-selected') === 'true';
        push(selected ? 'select' : 'deselect', {
          element_id: sel.id || null,
          element_tag: sel.tagName.toLowerCase(),
          meta: {
            group: sel.dataset.select,
            value: sel.dataset.value || null,
            selected: selected,
          },
        });
      }, 0);
    }
  }

  function isTrackableField(el) {
    if (!el || !el.matches) return false;
    if (!el.matches('input, textarea, select')) return false;
    if (el.type === 'password') return false;
    if (el.getAttribute && el.getAttribute('data-clarity-ignore') !== null) return false;
    return true;
  }

  function attachListeners() {
    if (listenersAttached) return;
    listenersAttached = true;

    // Scroll milestones (25/50/75/100 fired once per page view).
    var milestones = [25, 50, 75, 100];
    var fired = new Set();
    window.addEventListener('scroll', function () {
      var depth = getScrollDepth();
      if (depth > maxScroll) maxScroll = depth;
      for (var i = 0; i < milestones.length; i++) {
        var m = milestones[i];
        if (depth >= m && !fired.has(m)) {
          fired.add(m);
          push('scroll', { scroll_depth: m, meta: { depth: m } });
        }
      }
    }, { passive: true });

    // Reset scroll milestones on SPA route change.
    window.addEventListener('clarity:pageview-open', function () { fired.clear(); });

    // Clicks (capture so we still see cancelled clicks).
    document.addEventListener('click', trackClick, true);

    // Toggles / multi-selects.
    document.addEventListener('click', trackToggleOrSelect);

    // Real form submits — fires for <button type="submit">, <input type="submit">,
    // and Enter-key submits. Passive: we never preventDefault, so form behaviour
    // is untouched.
    document.addEventListener('submit', function (e) {
      if (isBlocked || pageBlocked) return;
      var form = e.target;
      if (!form || form.tagName !== 'FORM') return;
      try { doFormSubmit(form, true); } catch (_) {}
    }, true);

    // Submit-button clicks — covers forms whose "submit" button is
    // type="button" with a custom JS handler (e.g. Signize's .submitButton).
    // Emits form_submit_attempt for every submit-looking click. If it's a
    // real submit button, the browser's submit event will handle serialization
    // (deduped by recentlySubmitted). Otherwise we snapshot fields on click.
    document.addEventListener('click', function (e) {
      if (isBlocked || pageBlocked) return;
      var raw = e.target;
      if (!raw || !raw.closest) return;
      var btn = raw.closest(SUBMIT_BTN_SELECTOR);
      if (!btn) return;
      var form = btn.closest('form');
      if (!form) return;

      try {
        push('form_submit_attempt', {
          element_id: btn.id || null,
          element_tag: (btn.tagName || '').toLowerCase(),
          element_classes: safeClassName(btn),
          element_text: ((btn.innerText || btn.textContent || '').trim().slice(0, 512)) || null,
          meta: {
            form_name: formNameFor(form),
            form_selector: formSelectorFor(form),
          },
        });

        // <button> with no type attr defaults to submit; skip serialization here —
        // the native submit event will do it.
        var btnType = (btn.getAttribute && btn.getAttribute('type')) || '';
        var isRealSubmit =
          (btn.tagName === 'BUTTON' && btnType !== 'button') ||
          btnType === 'submit' ||
          btnType === 'image';
        if (isRealSubmit) return;

        // Fake-submit case: let any inline click handler mutate the form
        // first (e.g. append fields), then snapshot.
        setTimeout(function () {
          try { doFormSubmit(form, true); } catch (_) {}
        }, 50);
      } catch (_) {}
    }, true);

    // Form fields.
    var debounceMap = new WeakMap();

    document.addEventListener('focusin', function (e) {
      var el = e.target;
      if (!isTrackableField(el)) return;
      push('form_focus', {
        element_id: el.id || null,
        element_name: el.name || null,
        form_field_type: el.type || 'text',
      });
    });

    document.addEventListener('focusout', function (e) {
      var el = e.target;
      if (!isTrackableField(el)) return;
      var filled = !!(el.value && String(el.value).length);
      push('form_blur', {
        element_id: el.id || null,
        element_name: el.name || null,
        form_field_type: el.type || 'text',
        meta: { filled: filled },
      });
    });

    document.addEventListener('input', function (e) {
      var el = e.target;
      if (!isTrackableField(el)) return;
      var t = debounceMap.get(el);
      if (t) clearTimeout(t);
      debounceMap.set(el, setTimeout(function () {
        var rawVal = (el.type === 'checkbox' || el.type === 'radio') ? el.checked : el.value;
        var strVal = rawVal === null || rawVal === undefined ? '' : String(rawVal);
        push('form_input', {
          element_id: el.id || null,
          element_name: el.name || null,
          form_field_type: el.type || 'text',
          meta: { value: strVal, length: strVal.length },
        });
      }, 500));
    });

    // File uploads.
    document.addEventListener('change', function (e) {
      var el = e.target;
      if (!el || el.tagName !== 'INPUT' || el.type !== 'file') return;
      var f = el.files && el.files[0];
      if (!f) return;

      if (f.size > FILE_MAX_BYTES) {
        push('file_error', {
          element_id: el.id || null,
          element_name: el.name || null,
          meta: { filename: f.name, reason: 'size_over_limit', size_bytes: f.size },
        });
        return;
      }
      if (OK_FILE_MIMES.length && f.type && !OK_FILE_MIMES.includes(f.type)) {
        push('file_error', {
          element_id: el.id || null,
          element_name: el.name || null,
          meta: { filename: f.name, reason: 'invalid_type', size_bytes: f.size },
        });
        return;
      }
      push('file_upload', {
        element_id: el.id || null,
        element_name: el.name || null,
        meta: { filename: f.name, mime_type: f.type, size_bytes: f.size },
      });
    });

    // Clipboard.
    document.addEventListener('copy', function () {
      var length = 0;
      try {
        var s = document.getSelection && document.getSelection().toString();
        length = s ? s.length : 0;
      } catch (_) {}
      push('copy', { meta: { length: length } });
    });

    // Resize (debounced).
    var resizeT;
    window.addEventListener('resize', function () {
      clearTimeout(resizeT);
      resizeT = setTimeout(function () {
        push('resize', { meta: { width: window.innerWidth, height: window.innerHeight } });
      }, 300);
    });

    // Visibility.
    document.addEventListener('visibilitychange', function () {
      push('visibility_change', { meta: { state: document.hidden ? 'hidden' : 'visible' } });
      if (document.hidden) flush();
    });

    // Page exit → beacon close.
    window.addEventListener('pagehide', function () { closePageView('close', true); });

    // Idle-timeout recycle. Recycle *before* pushing the activity so the
    // event lands on the fresh session.
    var recycleIfStale = function () {
      var last = ssGet('clarity_last_activity_ts') || lsGet('clarity_last_activity_ts');
      var lastMs = last ? Date.parse(last) : 0;
      if (lastMs && (Date.now() - lastMs) > SESSION_TIMEOUT_MS) {
        ssRemove('clarity_session_uuid');
        lsRemove('clarity_session_uuid');
        sessionUuid = null;
        boot();
      } else {
        touch();
      }
    };
    ['mousedown', 'keydown', 'touchstart'].forEach(function (t) {
      document.addEventListener(t, recycleIfStale, { passive: true });
    });

    // SPA route change: patch history + popstate.
    patchHistory();
  }

  function closePageView(exitType, useBeacon) {
    if (isBlocked || !pageViewUuid) return Promise.resolve();
    var timeOnPage = Math.floor((Date.now() - pageEnteredAtMs) / 1000);
    // Flush events first so they land on this page view.
    var flushP = flush(useBeacon);
    var closeP = post('/pageview/close', {
      session_uuid: sessionUuid,
      page_view_uuid: pageViewUuid,
      left_at: nowIso(),
      time_on_page_seconds: timeOnPage,
      max_scroll_depth: maxScroll,
      exit_type: exitType,
    }, useBeacon).catch(function () {});
    return Promise.all([flushP, closeP]);
  }

  // ---------- SPA ----------
  var spaLock = false;
  function spaRouteChange() {
    if (isBlocked || spaLock) return Promise.resolve();
    spaLock = true;
    var prev = pageViewUuid;
    var timeOnPage = Math.floor((Date.now() - pageEnteredAtMs) / 1000);

    var closeP = prev
      ? flush().then(function () {
          return post('/pageview/close', {
            session_uuid: sessionUuid,
            page_view_uuid: prev,
            left_at: nowIso(),
            time_on_page_seconds: timeOnPage,
            max_scroll_depth: maxScroll,
            exit_type: 'navigation',
          }).catch(function () {});
        })
      : Promise.resolve();

    return closeP
      .then(function () {
        if (prev) previousPageViewUuid = prev;
        pageViewUuid = null;

        // Recompute soft block for the new URL. Skip opening a pageview
        // when navigating into a blocked path; resume on the next nav out.
        pageBlocked = isPathBlocked(location.pathname);
        if (pageBlocked) return null;

        // If we booted on a blocked page, the session was never started.
        if (!sessionUuid) {
          return getFingerprint().then(function (fp) {
            return startSession(null, fp).then(function () {
              if (!sessionUuid) return null;
              return openPageView();
            });
          });
        }
        return openPageView();
      })
      .then(function () {
        if (!pageBlocked && pageViewUuid) {
          try { window.dispatchEvent(new Event('clarity:pageview-open')); } catch (_) {}
        }
      })
      .catch(function () {})
      .then(function () { spaLock = false; });
  }

  function patchHistory() {
    var origPush = history.pushState;
    var origReplace = history.replaceState;
    history.pushState = function () {
      var r = origPush.apply(this, arguments);
      Promise.resolve().then(spaRouteChange);
      return r;
    };
    history.replaceState = function () {
      var r = origReplace.apply(this, arguments);
      Promise.resolve().then(spaRouteChange);
      return r;
    };
    window.addEventListener('popstate', function () { spaRouteChange(); });
  }

  // ---------- boot ----------
  function boot() {
    if (isBlocked) return Promise.resolve();

    pageBlocked = isPathBlocked(location.pathname);

    // Attach listeners + intervals unconditionally so that if the visitor
    // SPA-navigates from a blocked page to a tracked page, we can resume.
    attachListeners();
    if (!intervalsStarted) {
      intervalsStarted = true;
      setInterval(function () { flush(); }, FLUSH_INTERVAL_MS);
      setInterval(heartbeat, HEARTBEAT_INTERVAL_MS);
    }

    if (pageBlocked) return Promise.resolve();

    // Kick off the FingerprintJS CDN load in parallel with geo lookup (fire
    // and forget) so it's likely already resolved by the time startSession()
    // actually needs it — never blocks page rendering.
    if (!sessionUuid) initFingerprint();

    return fetchGeoWithTimeout()
      .catch(function () { return null; })
      .then(function (geo) {
        if (geo && geo.country && BLOCKED_COUNTRIES.indexOf(geo.country) !== -1) {
          isBlocked = true;
          return null;
        }
        if (sessionUuid) return null;
        return getFingerprint().then(function (fp) {
          return startSession(geo, fp).catch(function () {});
        });
      })
      .then(function () {
        if (isBlocked || pageBlocked || !sessionUuid) return null;
        return openPageView().catch(function () {});
      })
      .then(function () {
        if (isBlocked || pageBlocked || !pageViewUuid) return;
        try { window.dispatchEvent(new Event('clarity:pageview-open')); } catch (_) {}
      });
  }

  // ---------- public API ----------
  window.Clarity = {
    track: function (type, data) { push(type, data); },
    identify: function (email, name, phone, meta) {
      if (isBlocked || !sessionUuid) return Promise.resolve(null);
      return post('/identify', {
        session_uuid: sessionUuid,
        email: email,
        name: name || null,
        phone: phone || null,
        meta: meta || null,
      });
    },
    formSubmit: function (opts) {
      opts = opts || {};
      if (isBlocked || !sessionUuid || !pageViewUuid) return Promise.resolve(null);
      return post('/form-submit', {
        session_uuid: sessionUuid,
        page_view_uuid: pageViewUuid,
        form_name: opts.name,
        form_selector: opts.selector || null,
        fields: opts.fields || {},
        was_complete: opts.was_complete !== false,
        submitted_at: nowIso(),
      });
    },
    flush: function () { return flush(); },
    spaRouteChange: spaRouteChange,
    getSessionUuid: function () { return sessionUuid; },
    getPageViewUuid: function () { return pageViewUuid; },
    getVisitorUuid: function () { return visitorUuid; },
  };

  boot();
})();
